"use client";
/**
 * Shortlist — the menu-only counterpart to the cart.
 *
 * On a digital-menu link there is no ordering, but a customer still wants to
 * gather what they're interested in and see roughly what it comes to. v1 solved
 * this by letting the real cart fill up and then blocking checkout, which left
 * customers at a dead end with no way to submit. This is a separate concept:
 * saved items and an estimated total, with no checkout to reach.
 *
 * Structurally a sibling of `store/cart.tsx` — same reducer shape, same
 * branch-scoped persistence — but deliberately NOT built on it. `cart.tsx` sits
 * on the checkout-critical path and this feature has no server-side backstop,
 * so refactoring it to share ~30 lines would risk the paying flow to tidy a
 * flow that cannot take money. Extracting the common line type is a clean
 * follow-up if it ever earns its keep.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  type ReactNode,
} from "react";

import type { Tone } from "@/components/FoodImg";
import { useBranch } from "./branch";
import { track } from "@/lib/analytics";
import { scopedStorageKey } from "@/lib/storageScope";
import { branchIdentifier } from "@/lib/fulfillment";
import type {
  SelectedModifierOption,
  SelectedVariantOption,
} from "@/api/online-ordering-service/model";

/**
 * The subset of a cart line that describes an item. Omits the four
 * `recommendation*` attribution fields, which exist only to attribute *order*
 * conversions and have no meaning without an order.
 */
export type ShortlistLine = {
  id: string;
  itemId: string;
  name: string;
  categoryId: string;
  categoryName: string;
  price: number;
  qty: number;
  tone: Tone;
  imageUrl?: string | null;
  selectedModifiers: SelectedModifierOption[];
  selectedVariants: SelectedVariantOption[];
  modsLabel: string[];
  notes: string;
};

type ShortlistAction =
  | { type: "add"; line: ShortlistLine }
  | { type: "updateQty"; id: string; qty: number }
  | { type: "remove"; id: string }
  | { type: "clear" }
  | { type: "set"; lines: ShortlistLine[]; orderNote: string }
  | { type: "updateLine"; id: string; line: ShortlistLine }
  | { type: "setOrderNote"; note: string };

type ShortlistState = {
  lines: ShortlistLine[];
  orderNote: string;
};

/**
 * Two lines merge when they describe the same thing: same item, same note, and
 * the same variant + modifier selections regardless of the order they were
 * picked in.
 */
function isSameLine(a: ShortlistLine, b: ShortlistLine): boolean {
  if (a.itemId !== b.itemId) return false;
  if (a.notes !== b.notes) return false;

  if (a.selectedVariants.length !== b.selectedVariants.length) return false;
  const variantKey = (line: ShortlistLine) =>
    line.selectedVariants
      .map((v) => `${v.variantGroupId}:${v.variantOptionId}`)
      .sort()
      .join("|");
  if (variantKey(a) !== variantKey(b)) return false;

  if (a.selectedModifiers.length !== b.selectedModifiers.length) return false;
  const modifierKey = (line: ShortlistLine) =>
    line.selectedModifiers
      .map((m) => `${m.modifierGroupId}:${m.modifierOptionId}:${m.quantity}`)
      .sort()
      .join("|");
  return modifierKey(a) === modifierKey(b);
}

function reducer(
  state: ShortlistState,
  action: ShortlistAction,
): ShortlistState {
  switch (action.type) {
    case "add": {
      const existingIndex = state.lines.findIndex((l) => isSameLine(l, action.line));
      if (existingIndex !== -1) {
        const next = [...state.lines];
        const existing = next[existingIndex];
        next[existingIndex] = {
          ...existing,
          qty: existing.qty + action.line.qty,
        };
        return { ...state, lines: next };
      }
      return { ...state, lines: [...state.lines, action.line] };
    }
    case "updateQty":
      if (action.qty <= 0) return { ...state, lines: state.lines.filter((l) => l.id !== action.id) };
      return { ...state, lines: state.lines.map((l) =>
        l.id === action.id ? { ...l, qty: action.qty } : l,
      ) };
    case "updateLine":
      return { ...state, lines: state.lines.map((l) =>
        l.id === action.id ? action.line : l,
      ) };
    case "remove":
      return { ...state, lines: state.lines.filter((l) => l.id !== action.id) };
    case "clear":
      return { lines: [], orderNote: "" };
    case "set":
      return { lines: action.lines, orderNote: action.orderNote };
    case "setOrderNote":
      return { ...state, orderNote: action.note };
    default:
      return state;
  }
}

type ShortlistCtx = {
  shortlist: ShortlistLine[];
  orderNote: string;
  addToShortlist: (line: ShortlistLine) => void;
  updateQty: (id: string, qty: number) => void;
  updateLine: (id: string, line: ShortlistLine) => void;
  removeLine: (id: string) => void;
  clearShortlist: () => void;
  setOrderNote: (note: string) => void;
  shortlistCount: number;
  /** Estimated total. Never a price to be paid — there is no checkout. */
  shortlistTotal: number;
};

const ShortlistContext = createContext<ShortlistCtx | null>(null);

const PERSISTENCE_VERSION = 1;
const BASE_STORAGE_KEY = `shortlist_v${PERSISTENCE_VERSION}_lines`;
const NO_BRANCH_SCOPE = "__no_branch__";

function storageKeyFor(
  slug: string | null | undefined,
  branchScope: string | null | undefined,
): string {
  return scopedStorageKey(
    `${BASE_STORAGE_KEY}:${branchScope || NO_BRANCH_SCOPE}`,
    slug,
  );
}

function isValidLine(x: unknown): x is ShortlistLine {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.id === "string" &&
    typeof o.itemId === "string" &&
    typeof o.name === "string" &&
    typeof o.price === "number" &&
    typeof o.qty === "number" &&
    Array.isArray(o.selectedModifiers) &&
    Array.isArray(o.selectedVariants) &&
    Array.isArray(o.modsLabel)
  );
}

function readFromStorage(key: string): ShortlistState {
  if (typeof window === "undefined") return { lines: [], orderNote: "" };
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return { lines: [], orderNote: "" };
    const parsed: unknown = JSON.parse(raw);
    
    let lines: ShortlistLine[] = [];
    let orderNote = "";

    if (Array.isArray(parsed)) {
      // Legacy format
      lines = parsed.filter(isValidLine);
    } else if (parsed && typeof parsed === "object") {
      const obj = parsed as Record<string, unknown>;
      if (Array.isArray(obj.lines)) {
        lines = obj.lines.filter(isValidLine);
      }
      if (typeof obj.orderNote === "string") {
        orderNote = obj.orderNote;
      }
    }
    return { lines, orderNote };
  } catch {
    // A corrupt or partially-written entry should cost the customer their
    // shortlist, not the whole menu.
    return { lines: [], orderNote: "" };
  }
}

export function ShortlistProvider({ children }: { children: ReactNode }) {
  const { branchSlug, branch } = useBranch();
  const branchScope = branchIdentifier(branch) ?? null;
  const storageKey = storageKeyFor(branchSlug, branchScope);

  const [state, dispatch] = useReducer(reducer, { lines: [], orderNote: "" });
  // Guards the persist effect: without it the initial empty state would
  // overwrite stored lines before the hydrate effect has run.
  const [hydratedKey, setHydratedKey] = useState<string | null>(null);

  useEffect(() => {
    const stored = readFromStorage(storageKey);
    dispatch({ type: "set", lines: stored.lines, orderNote: stored.orderNote });
    setHydratedKey(storageKey);
  }, [storageKey]);

  useEffect(() => {
    if (hydratedKey !== storageKey) return;
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // Private-mode / quota failures are not worth breaking the menu over.
    }
  }, [state, storageKey, hydratedKey]);

  const value = useMemo<ShortlistCtx>(() => {
    return {
      shortlist: state.lines,
      orderNote: state.orderNote,
      addToShortlist: (line) => {
        dispatch({ type: "add", line });
        track("item_added_to_shortlist", {
          item_id: line.itemId,
          item_name: line.name,
          category_id: line.categoryId,
          qty: line.qty,
          price: line.price,
        });
      },
      updateQty: (id, qty) => dispatch({ type: "updateQty", id, qty }),
      updateLine: (id, line) => dispatch({ type: "updateLine", id, line }),
      removeLine: (id) => {
        dispatch({ type: "remove", id });
        track("item_removed_from_shortlist", { line_id: id });
      },
      clearShortlist: () => {
        dispatch({ type: "clear" });
        track("shortlist_cleared", { line_count: state.lines.length });
      },
      setOrderNote: (note) => dispatch({ type: "setOrderNote", note }),
      shortlistCount: state.lines.reduce((sum, l) => sum + l.qty, 0),
      shortlistTotal: state.lines.reduce((sum, l) => sum + l.price * l.qty, 0),
    };
  }, [state]);

  return (
    <ShortlistContext.Provider value={value}>
      {children}
    </ShortlistContext.Provider>
  );
}

export function useShortlist(): ShortlistCtx {
  const ctx = useContext(ShortlistContext);
  if (!ctx) {
    throw new Error("useShortlist must be used within a ShortlistProvider");
  }
  return ctx;
}
